import React, { useState, useEffect } from 'react';
import Card from './Card';
import Button from './Button';
import InputField from './InputField';
import ToggleSwitch from './ToggleSwitch';
import CloseIcon from '../icons/CloseIcon';
import PlusIcon from '../icons/PlusIcon';
import MinusIcon from '../icons/MinusIcon';

interface AddNewSeatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    startDate: string;
    endDate: string;
    rows: number;
    cols: number;
    isPairedSeating: boolean;
    boardPosition: 'top' | 'bottom';
  }) => void;
  mode?: 'create' | 'edit'; // 새로 추가
  initialData?: {
    startDate?: string;
    endDate?: string;
    rows?: number;
    cols?: number;
    isPairedSeating?: boolean;
    boardPosition?: 'top' | 'bottom';
  };
}

const AddNewSeatDialog: React.FC<AddNewSeatDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  mode = 'create', // 기본값은 'create'
  initialData
}) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [rows, setRows] = useState<number>(4);
  const [cols, setCols] = useState<number>(6);
  const [isPairedSeating, setIsPairedSeating] = useState<boolean>(false);
  const [boardPosition, setBoardPosition] = useState<'top' | 'bottom'>('top');

  // 초기 데이터 설정
  useEffect(() => {
    if (isOpen && initialData) {
      setStartDate(initialData.startDate || '');
      setEndDate(initialData.endDate || '');
      setRows(initialData.rows || 4);
      setCols(initialData.cols || 6);
      setIsPairedSeating(initialData.isPairedSeating || false);
      setBoardPosition(initialData.boardPosition || 'top');
    } else if (isOpen) {
      // 기본값으로 초기화
      setStartDate('');
      setEndDate('');
      setRows(4);
      setCols(6);
      setIsPairedSeating(false);
      setBoardPosition('top');
    }
  }, [isOpen, initialData]);

  // 총 자리 수 계산
  const totalSeats = rows * cols;

  // 행 수 증가/감소
  const handleRowsChange = (delta: number) => {
    const newRows = Math.max(1, rows + delta);
    setRows(newRows);
  };

  // 열 수 증가/감소
  const handleColsChange = (delta: number) => {
    const newCols = Math.max(1, cols + delta);
    setCols(newCols);
  };

  // 저장 핸들러
  const handleSave = () => {
    if (!startDate || !endDate) {
      alert('시작 날짜와 종료 날짜를 모두 선택해주세요.');
      return;
    }

    onSave({
      startDate,
      endDate,
      rows,
      cols,
      isPairedSeating,
      boardPosition
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: 'Pretendard'
    }}>
      <Card
        width="450px"
        padding="0"
        borderRadius="16px"
        background="var(--Color-background-normal, white)"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
        style={{
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          width: '394px',
          margin: '28px',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <div style={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '30px',
                wordWrap: 'break-word'
              }}>
                {mode === 'edit' ? '자리 배치 설정하기' : '새 자리 배치하기'}
              </div>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px',
                wordWrap: 'break-word'
              }}>
                원하시는 반 자리 옵션을 설정해 주세요.
              </div>
            </div>
            
            {/* 닫기 버튼 */}
            <div 
              style={{
                padding: '4px',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={onClose}
            >
              <CloseIcon width={13.20} height={13.20} color="#0D0D0D" />
            </div>
          </div>

          {/* 본문 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {/* 기간 설정 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '24px',
                wordWrap: 'break-word'
              }}>
                기간 설정
              </div>
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <InputField
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="시작 날짜 선택"
                  width="100%"
                  height="48px"
                />
                <InputField
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="종료 날짜 선택"
                  width="100%"
                  height="48px"
                />
              </div>
            </div>

            {/* 행/열 설정 */}
            <div style={{
              display: 'flex',
              gap: '8px'
            }}>
              {/* 행 설정 */}
              <div style={{
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '24px',
                  wordWrap: 'break-word'
                }}>
                  행 설정
                </div>
                <div style={{
                  display: 'flex',
                  padding: '12px 16px',
                  background: 'var(--Color-fill-white, white)',
                  borderRadius: '8px',
                  border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      width: '24px',
                      height: '24px'
                    }}
                    onClick={() => handleRowsChange(-1)}
                  >
                    <MinusIcon width={13.20} height={1.20} color="#0D0D0D" />
                  </div>
                  <div style={{
                    flex: '1 1 0',
                    textAlign: 'center',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '19.50px',
                    wordWrap: 'break-word'
                  }}>
                    {rows}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      width: '24px',
                      height: '24px'
                    }}
                    onClick={() => handleRowsChange(1)}
                  >
                    <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                  </div>
                </div>
              </div>

              {/* 열 설정 */}
              <div style={{
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '24px',
                  wordWrap: 'break-word'
                }}>
                  열 설정
                </div>
                <div style={{
                  display: 'flex',
                  padding: '12px 16px',
                  background: 'var(--Color-fill-white, white)',
                  borderRadius: '8px',
                  border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      width: '24px',
                      height: '24px'
                    }}
                    onClick={() => handleColsChange(-1)}
                  >
                    <MinusIcon width={13.20} height={1.20} color="#0D0D0D" />
                  </div>
                  <div style={{
                    flex: '1 1 0',
                    textAlign: 'center',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '19.50px',
                    wordWrap: 'break-word'
                  }}>
                    {cols}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      width: '24px',
                      height: '24px'
                    }}
                    onClick={() => handleColsChange(1)}
                  >
                    <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                  </div>
                </div>
              </div>
            </div>

            {/* 총 자리 수 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '24px',
                wordWrap: 'break-word'
              }}>
                총 자리 수
              </div>
              <div style={{
                display: 'flex',
                padding: '12px 16px',
                background: 'var(--Color-fill-white, white)',
                borderRadius: '8px',
                border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <div style={{
                  flex: '1 1 0',
                  textAlign: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.50px',
                  wordWrap: 'break-word'
                }}>
                  {totalSeats}
                </div>
              </div>
            </div>

            {/* 짝 배치 / 교탁 위치 */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              {/* 짝 배치 */}
              <div style={{
                width: '191px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '24px',
                  wordWrap: 'break-word'
                }}>
                  짝 배치
                </div>
                <ToggleSwitch
                  checked={isPairedSeating}
                  onChange={setIsPairedSeating}
                  size="medium"
                />
              </div>

              {/* 교탁/칠판 위치 */}
              <div style={{
                width: '191px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '24px',
                  wordWrap: 'break-word'
                }}>
                  교탁/칠판 위치
                </div>
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <div
                    style={{
                      padding: '4px 8px',
                      background: boardPosition === 'top' 
                        ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                        : 'transparent',
                      color: boardPosition === 'top' 
                        ? 'var(--Color-text-inverse, white)' 
                        : 'var(--Color-text-normal, #161616)',
                      borderRadius: '4px',
                      border: boardPosition === 'top' 
                        ? 'none' 
                        : '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '11px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '16.50px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                    onClick={() => setBoardPosition('top')}
                  >
                    상단
                  </div>
                  <div
                    style={{
                      padding: '4px 8px',
                      background: boardPosition === 'bottom' 
                        ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                        : 'transparent',
                      color: boardPosition === 'bottom' 
                        ? 'var(--Color-text-inverse, white)' 
                        : 'var(--Color-text-normal, #161616)',
                      borderRadius: '4px',
                      border: boardPosition === 'bottom' 
                        ? 'none' 
                        : '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '11px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '16.50px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                    onClick={() => setBoardPosition('bottom')}
                  >
                    하단
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 버튼 영역 */}
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            <Button
              variant="cancel"
              width="124px"
              onClick={onClose}
            >
              취소
            </Button>
            <Button
              variant="submit"
              width="262px"
              onClick={handleSave}
            >
              {mode === 'edit' ? '저장' : '수정'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddNewSeatDialog;
